import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import {Subscription}                 from 'rxjs';

import {
  ActivatedRoute,
  Router
}                                     from '@angular/router';

import {ActionService}                from "@/services/action.service";
import {Logger}                       from '@/util/logger';
import {PanoramaService}              from "@/services/panorama.service";
import {Subdivision}                  from "@/models/subdivision";
import {first}                        from "rxjs/operators";
import {errorAsText}                  from "@/util/util";
import {Panorama}                     from "@/models/panorama";
import {PanoramaSelection}            from "@/models/panorama.selection";
import {PanoramaSelectorToggleAction} from "@/components/krpano/actions/panorama.selector.toggle.action";

import * as $                         from "jquery";

@Component({
  standalone: false,
  selector:    'pano-selector',
  template: `
<div id="pano-selector-wrapper" class="pano-selector-wrapper light">

  <div class="pano-selector-row">
    <p-dropdown
      [disabled]="subOptions().length == 0"
      [options]="subOptions()"
      autoDisplayFirst="true"
      optionLabel="label"
      optionValue="value"
      [(ngModel)]="_selectedSub"
      scrollHeight="200"
      (onChange)="subChange($event)"
    >
    </p-dropdown>
  </div>

  <div class="pano-selector-row">
    <p-dropdown
      [disabled]="timeOptions().length == 0"
      [options]="timeOptions()"
      optionLabel="label"
      optionValue="value"
      [placeholder]="firstTimeline()"
      autoDisplayFirst="true"
      [(ngModel)]="_selectedTime"
      (onChange)="timeChange($event)"
      placeholder="Timeline"
      scrollHeight="200"
    >
    </p-dropdown>
  </div>

  <div class="pano-selector-row">

    <p-inputgroup>

      <input
        pInputText
        pKeyFilter="num"
        placeholder="Milepost"
        id="milepostinput"
        [disabled]="!milesEnabled()"
        [(ngModel)]="_milePost"
        (keydown)="checkMileKey($event)"
        type="text"
        (change)="mileChanged($event)"
      />

      <p-button
        [disabled]="!milesEnabled()"
        (click)="navigate()"
        type="button"
        pButton
        pRipple
        label="GO!"
      />

    </p-inputgroup>

  </div>

</div>
  `
})

/**
 *
 * @class PanoramaSelector
 *
 * @brief the filter/navigator used to pick out panorama to jump to
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2022
 *
 */
export class PanoramaSelector implements OnInit, OnDestroy, AfterViewInit {

  /** are we showing this control widget? */
  private _visible: boolean                               = false;

  public visibility: string                               = 'hidden';

  /** the subdivision options */
  public _subdivisions : {label: string, value: string}[] = [];

  /** the current set of subdivisions and their possible timelines */
  private _subs: Subdivision[]                            = [];

  /** the currently selected subdivision */
  public _selectedSub : any                               = null;

  /** our subscription to changes of the set of subdivisions */
  private  subWatch: Subscription;

  /** the timelines options */
  public _timeOptions : {label: string, value: string}[]  = [];

  /** currently selected timeline */
  public _selectedTime : any                              = null;

  /** currently selected milepost */
  public _milePost : number|string                        = 'Milepost';

  /** is the milepost input disabled? */
  public _mileEnabled : boolean                           = true;

  public _handlersRegistered: boolean                     = false;
  /**
   * standard constructor
   *
   * @param router  in case we need to redirect the page
   * @param actions the GUI actions we can invoke
   * @param route   the active URL
   * @param panos   the panorama service
   */
  constructor(
    private router:  Router,
    private actions: ActionService,
    private route:   ActivatedRoute,
    private panos:   PanoramaService,
    private cdRef:  ChangeDetectorRef
  ) {

    Logger.debug("[PanoramaSelector] constructing...");

    this.actions.add(new PanoramaSelectorToggleAction(this));
  }

  /* on creation subscribe to the alert service so we get updates when alerts happen from anywhere */
  ngOnInit() {

    let ps: PanoramaSelector = this;

    Logger.debug('[PanoramaSelector] init...');

    /* listen to the panorama service for new definitions of the corridor / subdivision tree */

    ps.subWatch = ps.panos.onSubvidivisions()
      .subscribe((subs: Subdivision[]) => {

        Logger.debug('[PanoramaSelector] subdivisions received: ', subs);

        /* usual setup ... */

        ps._subs         = subs;

        /* sort alphabetically before we actually use */

        ps._subs.sort((s1: Subdivision, s2: Subdivision): number => {

          return (s1.name.localeCompare(s2.name));

        });

        /* use the new subdivision list */

        ps._subdivisions = [{label: 'Select Subdivision', value: null}, ...ps._subs.map( x => { return {
          label: x.name,
          value: x.name
        };})];

        ps.show();

        /* check if we have things selected already... */

        let sel: PanoramaSelection = ps.panos.selection();

        if (sel && sel.sub) {

          let selMile: number   = Number(sel.mile.toFixed(4));

          if ((ps._milePost     == selMile) &&
              (ps._selectedTime == sel.timeline) &&
              (ps._selectedSub  == sel.sub)) {

            Logger.debug(`[PanoramaSelector] select old == new, skipping.`);

            return ;
          }

          ps._selectedSub = sel.sub;

          ps.switchToSub(ps.subdivision());

          ps._selectedTime = sel.timeline;
          ps._milePost     = selMile;

          Logger.debug(`[PanoramaSelector] select after load ${this._selectedSub} : ${this._selectedTime} : ${this._milePost}`);

          ps.cdRef.detectChanges();

        } else {

          ps.clear();

          ps._selectedSub  = null;
        }

      });
  }

  ngAfterViewInit() {

    Logger.debug("[PanoramaSelector] after view init...");

    let ps: PanoramaSelector = this;

    setTimeout( () => {

      ps.registerEventHandlers();

    }, 1000);

  }

  /** react to the milepost input getting the focus */
  registerEventHandlers() {

    Logger.debug("[PanoramaSelector] registering event callbacks...");

    let ps: PanoramaSelector = this;
    let input1: JQuery = $(`overwatch #pano-selector-wrapper #milepostinput`);
    let input2: JQuery = $(`panorama #pano-selector-wrapper  #milepostinput`);

    if (ps._handlersRegistered) {

      /* already registered */

      return ;
    }

    ps._handlersRegistered = true;

    /* wait for the DOM to be ready ... */

    Logger.debug(`[PanoramaSelector] waiting for the <pano-selector> to be added to DOM...`);

    let timer = setInterval(function() {

      if ((input1.length == 0) && (input2.length == 0))  {

        /* doesn't exist yet, keep checking... */

        return;
      }

      Logger.debug(`[PanoramaSelector] <pano-selector> created! registering event handlers...`);

      /*
       * when the milepost input gains the focus, if it still has the place holder,
       * get rid of the place holder.
       *
       */

      $(input1).on('focus', () => {

        if (typeof ps._milePost == 'string') {

          ps._milePost = 0.0;
        }

      });

      $(input2).on('focus', () => {

        if (typeof ps._milePost == 'string') {

          ps._milePost = 0.0;
        }

      });

      /* clear the interval timer */

      clearInterval(timer);

    }, 100);

  }

  /* clean up at the end */
  ngOnDestroy() {

    Logger.debug("[PanoramaSelector] destroying...");

    if (this.subWatch) {

      this.subWatch.unsubscribe();
    }

  }

  /** fetch all possible subdivision options */
  subOptions(): {label: string, value: string}[] {

    return this._subdivisions;
  }

  /** fetch the current subdivision selected */
  subdivision(): Subdivision {

    let ps: PanoramaSelector = this;

    return  ps._subs.find(x => x.name == ps._selectedSub);
  }

  switchToSub(s: Subdivision) {

    let ps: PanoramaSelector = this;

    Logger.debug("[PanoramaSelector] switch to sub: ", s);

    ps._timeOptions = [...s.timelines.map(x => {

      /* make a nicer label for the Y-m-d value */

      let dateParts: string[] = x.split("-");
      let d: Date             = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2].substr(0,2)));

      return {
        label: {
          0: "Jan",
          1: "Feb",
          2: "Mar",
          3: "Apr",
          4: "May",
          5: "Jun",
          6: "Jul",
          7: "Aug",
          8: "Sep",
          9: "Oct",
          10: "Nov",
          11: "Dec"
        }[d.getMonth()] + ' ' + d.getFullYear(),
        value: x
      };

    })];

    ps._selectedTime = s.timelines[0];

    ps.cdRef.detectChanges();
  }

  clear() {

    let ps: PanoramaSelector = this;

    ps.panos.select(null);

    ps._selectedSub  = null;
    ps._selectedTime = null;
    ps._timeOptions  = [];
    ps._milePost     = 'Milepost';

    Logger.debug("[PanoramaSelector] cleared.");

    ps.cdRef.detectChanges();
  }

  /** new time (maybe none) was selected */
  timeChange(e) {

    let ps: PanoramaSelector = this;

    Logger.debug("[PanoramaSelector] timeline selected: ", ps._selectedTime);

    /* not supporting it for now; users expect action when mile changes */

    return ;

    /*
     * if the subdivision and mile are already set, then the intention is to
     * reload wit this different timeline.  If mile isn't set yet, then they
     * are in the process of selecting sub => timeline => mile and we don't
     * do anything.
     *
     */

    if (ps._selectedSub && ps._milePost) {

      Logger.debug("[PanoramaSelector] sub and mile are already set, time changed, finding nearest...");

      /*
       * use the nearest method on the current co-ordinates...but hew timeline, once we have that
       * panorama...we can then do the usual jump to a new panorama.
       *
       */

      let pano: Panorama = ps.panos.current();

      ps.panos.nearest(pano.x, pano.y, ps._selectedSub, ps._selectedTime)
        .pipe(first())
        .subscribe((data: any) => {

            let meta: Panorama = Panorama.from(data.result);

            if (data.result) {

              /*
               * we still have to update the input, even if aren't going to jump, so the user knows something,
               * rather than nothing happened.
               *
               */

              let oldMile: number = meta.mile;
              ps._milePost = meta.mile.toFixed(4);

              /* jump little frog! */

              Logger.debug(`[PanoramaSelector] timeline change jump to sub=${ps._selectedSub} : time=${ps._selectedTime} : mp=${ps._milePost}: `, meta);

              let cp: Panorama = ps.panos.current();

              if (cp && (cp.id == meta.id)) {

                Logger.warning(`[PanoramaSelector] via time shift: new mile input ${oldMile} => ${ps._milePost} leads to same panorama ${meta.id}, no action.`);

                return ;
              }

              ps.panos.panoramaNavigate(meta.id);

            } else {

              let msg: string = `no panorama found for sub=${ps._selectedSub} : time=${ps._selectedTime} : x=${pano.x} : y=${pano.y}`;

              Logger.error(`[PanoramaSelector] can't find nearest time panorama: `, msg);

              $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${msg}</p>`);
            }
          },
          (error) => {

            /* something went horribly, horribly wrong! */

            Logger.error(`[PanoramaSelector] can't find nearest (by timeline) panorama: `, errorAsText(error));

            $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${errorAsText(error)}</p>`);

          });
    }


  }

  /** new subdivision (maybe none) was selected */
  subChange(e) {

    let ps: PanoramaSelector = this;

    /* reset the options for the timeline dropdown */

    ps._milePost     = 'Milepost';
    ps._selectedTime = null;

    if (!ps._selectedSub) {

      ps._timeOptions  = [];

      return ;
    }

    ps.switchToSub(ps.subdivision())

    Logger.debug("[PanoramaSelector] new timelines: ", ps.timeOptions());
  }

  /** fetch all possible timelines for the current sub */
  timeOptions(): {label: string, value: string}[] {

    return this._timeOptions;
  }

  /** fetch the first possible timeline for the current sub */
  firstTimeline() {

    let ps: PanoramaSelector = this;

    if (ps._timeOptions.length == 0) {

      return null;
    }

    return ps._timeOptions[0].value;
  }

  /** css "visibility" of this control widget */
  visible(): string {

    return this.visibility;
  }

  /** as they type in the input for milepost, if they press ENTER, we navigate */
  checkMileKey(e: any) {

    let ps: PanoramaSelector = this;

    if (e.key == 'Enter') {

      ps.navigate();
    }
  }

  /** when the milepost input changes, make sure its a legit value */
  mileChanged(e) {

    let ps: PanoramaSelector = this;

    /* check for bogus values */

    if (!ps._milePost) {

      ps._milePost = 0.0;
    }

    if (typeof ps._milePost != 'number') {

      ps._milePost = Number(ps._milePost);
    }

    /* round to 2 decimal places */

    ps._milePost = Number(ps._milePost.toFixed(4));

    Logger.debug(`[PanoramaSelector] new mile value: `, ps._milePost);

    ps.cdRef.detectChanges();

  }

  /** to use the mile jump, we have to have a sub and timeline selected */
  milesEnabled() {

    let ps: PanoramaSelector = this;

    if (!ps._selectedSub || !ps._selectedTime) {

      return false;
    }

    return true;
  }

  /** based on whatever milepost they have entered, navigate! */
  navigate() {

    let ps: PanoramaSelector = this;

    Logger.debug(`[PanoramaSelector] navigating!`, ps._milePost);

    let mile: number = 0;

    if (typeof ps._milePost == 'number') {

      mile = ps._milePost;

    } else {

      mile = parseFloat(ps._milePost);
    }

    ps.panos.mile(mile, ps._selectedSub, ps._selectedTime)
      .pipe(first())
      .subscribe(
        (data: any) => {

          let meta: Panorama = Panorama.from(data.result);

          if (data.result) {

            /*
             * we still have to update the input, even if aren't going to jump, so the user knows something,
             * rather than nothing happened.
             *
             */

            ps._milePost = meta.mile.toFixed(4);

            /* jump little frog! */

            Logger.debug(`[PanoramaSelector] sub and timeline jump to sub=${ps._selectedSub} : time=${ps._selectedTime} : mp=${ps._milePost}: `, meta);

            let cp: Panorama = ps.panos.current();

            if (cp && (cp.id == meta.id)) {

              Logger.warning(`[PanoramaSelector] new mile input ${mile} => ${meta.mile} leads to same panorama ${meta.id}, no action.`);

              return ;
            }

            ps.panos.panoramaNavigate(meta.id);

            ps.cdRef.detectChanges();

          } else {

            let msg: string = `no panorama found for sub=${ps._selectedSub} : time=${ps._selectedTime} : mp=${ps._milePost}`;

            Logger.error(`[PanoramaSelector] can't find nearest (mile) panorama: `, msg);

            $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${msg}</p>`);
          }
        },

        (error) => {

          /* something went horribly, horribly wrong! */

          Logger.error(`[PanoramaSelector] can't find nearest (by mile) panorama: `, errorAsText(error));

          $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${errorAsText(error)}</p>`);

        });

  }

  /** autofocus on the milepost input, so the user can just go directly there with keyboard */
  milePostFocus() {

    let ps: PanoramaSelector = this;

    Logger.debug(`[PanoramaSelector] focusing on milepost...`);

    /*
     * this can only happen when we are on the panorama viewer page, so the select is
     * already setup with a subdivision and timeline; but if they did clear the
     * subdivision, then we just ignore the request.
     *
     */

    if (!ps._selectedSub) {

      /* nothing we can do they cleared the subdivision, we can't autopick from the subdivisions */

      return ;
    }

    if (!ps._selectedTime) {

      /* if there is no timeline yet, we can default to the first one for this subdivision */

      ps.switchToSub(ps._selectedSub);
    }


    if (!ps.isVisible()) {

      /* show the selector if its currently hidden */

      this.toggle();
    }

    /* finally, let the milepost input field take the focus ... */

    $(`#milepostinput`).focus();

    ps.cdRef.detectChanges();
  }

  /** flip the visibility state */
  toggle() {

    let ps: PanoramaSelector = this;

    if (ps.isVisible()) {

      ps.hide();

      return ;
    }

    ps.show();

  }

  isVisible() {

    return this._visible;
  }

  hide() {

    let ps: PanoramaSelector = this;

    ps._visible   = false;
    ps.visibility = 'hidden';

    $('#pano-selector-wrapper').css('visibility', ps.visibility);

    Logger.debug("[PanoramaSelector] hidden.");

    ps.cdRef.detectChanges();
  }

  show() {

    let ps: PanoramaSelector = this;

    ps._visible   = true;
    ps.visibility = 'visible';

    $('#pano-selector-wrapper').css('visibility', ps.visibility);

    Logger.debug("[PanoramaSelector] shown.");

    ps.cdRef.detectChanges();
  }
}
